import React from 'react';
import { palettes, LegalBody, RegularTitle } from '@indigo-ag/component-module';
import { faCheck, faMinusCircle, faTimesCircle, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { createStylesHook, utils } from 'shared';

export type NotificationSectionStatus = 'warning' | 'success' | 'error';

const notificationStatusIndicators: {
  [key in NotificationSectionStatus]: {
    colors: { borderColor: string; backgroundColor: string };
    icon: IconDefinition;
  };
} = {
  warning: {
    colors: {
      borderColor: palettes.brand.yellow.base,
      backgroundColor: palettes.brand.yellow.lightest
    },
    icon: faMinusCircle
  },
  success: {
    colors: {
      borderColor: palettes.brand.green.darker,
      backgroundColor: palettes.brand.green.lightest
    },
    icon: faCheck
  },
  error: {
    colors: {
      borderColor: palettes.brand.red.darker,
      backgroundColor: palettes.brand.red.lightest
    },
    icon: faTimesCircle
  }
};

const useStyles = createStylesHook({
  wrapper: {
    borderRadius: 6,
    border: '1px solid white',
    borderTop: '4px solid white',
    backgroundColor: 'white',
    marginBottom: 20,
    '&.warning': {
      ...notificationStatusIndicators.warning.colors
    },
    '&.success': {
      ...notificationStatusIndicators.success.colors
    },
    '&.error': {
      ...notificationStatusIndicators.error.colors
    }
  },
  container: {
    display: 'flex',
    marginBottom: 20
  }
});

export interface NotificationSectionProps {
  title: string;
  subtitle?: string;
  status: NotificationSectionStatus;
  updateBy?: string;
  updatedAt?: Date;
}

export const NotificationSection: React.FunctionComponent<NotificationSectionProps> = ({
  children,
  title,
  subtitle,
  status,
  updateBy,
  updatedAt
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  return (
    <div className={`${classes.wrapper} ${status}`}>
      <div style={{ padding: 20, paddingRight: 40 }}>
        <div className="flex flex-column width-100">
          <div className="flex">
            <div style={{ marginRight: 14 }}>
              <FontAwesomeIcon
                icon={notificationStatusIndicators[status].icon}
                style={{
                  fontSize: 20,
                  color: notificationStatusIndicators[status].colors.borderColor
                }}
              />
            </div>
            <div className="flex flex-column width-100">
              <div className="flex flex-column" style={{ marginBottom: 20 }}>
                <RegularTitle className="text-transform-capitalize">{title}</RegularTitle>
                {subtitle && (
                  <LegalBody className="text-transform-uppercase-first-letter" style={{ marginTop: 4 }}>
                    {subtitle}
                  </LegalBody>
                )}
              </div>
              <div>{children}</div>
            </div>
          </div>
          {(updateBy || updatedAt) && (
            <LegalBody style={{ marginTop: 20 }}>
              {updateBy || null}
              {updateBy && updatedAt ? ' - ' : null}
              {updatedAt ? utils.date.formatDate(updatedAt) : null}
            </LegalBody>
          )}
        </div>
      </div>
    </div>
  );
};
